import { ContactHandle } from '@/services/types/treatment-types';

/**
 * Filter and sort legacy phone handles from a provider's contact handles.
 * Prioritizes handles with "Records" or "Bills" in their name/description.
 *
 * @param handles - Array of contact handles from the provider
 * @returns Sorted array of phone handles with priority labels
 */
export function getPrioritizedPhoneHandles(
  handles: ContactHandle[] | undefined | null,
): Array<ContactHandle & { isPriority: boolean }> {
  if (!handles || !Array.isArray(handles) || handles.length === 0) {
    return [];
  }

  try {
    // Filter for phone handles only (with null safety)
    const phoneHandles = handles.filter(
      (h) => h && h.handle_type === 'PHONE' && h.value,
    );

    if (phoneHandles.length === 0) {
      return [];
    }

    // Mark handles as priority if they contain "Records" or "Bills" (case-insensitive)
    const priorityKeywords = /records|bills/i;
    const handlesWithPriority = phoneHandles.map((handle) => ({
      ...handle,
      isPriority: !!(handle.name && priorityKeywords.test(handle.name)),
    }));

    // Sort: priority handles first, then alphabetically by name
    return handlesWithPriority.sort((a, b) => {
      // Priority handles come first
      if (a.isPriority && !b.isPriority) return -1;
      if (!a.isPriority && b.isPriority) return 1;

      // Then sort by name (if available)
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    });
  } catch (error) {
    console.error('Error in getPrioritizedPhoneHandles:', error);
    return [];
  }
}

/**
 * Format a phone handle for display in a dropdown or list
 */
export function formatPhoneHandleLabel(handle: ContactHandle): string {
  if (handle.name) {
    return `${handle.name} - ${handle.value}`;
  }
  return handle.value;
}
